import React, { useMemo, useCallback } from 'react';
import { Button, Toast } from 'antd-mobile';
import { useLocation, useNavigate } from 'react-router-dom';
import CreateTaskApi from '@/fetch/business/spider-web/createTask';
import { HttpStatusCode } from '@/fetch/core/constant';
import { showErrorToast } from '@/utils/toast';
import { withAsyncLock } from '@/utils';
import { showModal } from '@/components/Modal';
import { BusinessType, VehicleScheduleState } from '@/utils/enum';
import {
  BLOCK_ROUTE_NAVIGATION,
  UNBLOCK_ROUTE_NAVIGATION
} from '@/utils/constant';
import { sendGlobalEvent } from '@/utils/emit';
import { AnyFunc } from '@/utils/types';
import useVehicleIssueValidator from '@/utils/hooks/useVehicleIssueValidator';
import { ExtraEntranceInfoContext } from '../../utils/type';

const createTaskApi = new CreateTaskApi();

interface SpiderWebEntranceProps {
  setPopoverVisible: AnyFunc;
  extraEntranceInfoContext: ExtraEntranceInfoContext;
}

/**
 * 蛛网入口
 * 三个点的入口内部的按钮
 * @param {SpiderWebEntranceProps} props
 * @return {*}
 */
function SpiderWebEntrance(props: SpiderWebEntranceProps) {
  const { setPopoverVisible, extraEntranceInfoContext } = props;
  const { isGeneralTransportSingleVehicle, spiderWebNavbarInfo } =
    extraEntranceInfoContext || {};

  const navigate = useNavigate();
  const location = useLocation();

  // 获取url上的车牌号
  const { vehicleName } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const vehicleName = searchParams.get('vehicleName') || '';
    return { vehicleName };
  }, [location.search]);

  // 校验当前车辆是否存在问题
  const { validateVehicleHasNoIssue } = useVehicleIssueValidator({
    vehicleName,
    fromPage: 'singleVehicle',
    businessType: BusinessType.SPIDER_WEB
  });

  /**
   * 开始创建蛛网任务
   * 先校验车号是否存在
   */
  const startCreateTask = () => {
    if (!vehicleName) {
      showErrorToast('车牌号获取失败，请重新进入页面');
      return;
    }
    createTask(vehicleName);
  };

  /**
   * 创建蛛网任务
   */
  const createTask = useCallback(
    withAsyncLock(async (vehicleName: string) => {
      // 开始创建任务前，阻止路由跳转
      sendGlobalEvent(BLOCK_ROUTE_NAVIGATION);
      try {
        const res = await createTaskApi.createTask(vehicleName);
        Toast.show({
          icon: 'loading',
          content: '加载中…',
          maskClickable: false,
          duration: 0
        });
        if (res?.code === HttpStatusCode.Success) {
          if (isGeneralTransportSingleVehicle) {
            history.replaceState(
              null,
              '',
              `/spider-web/singleVehicle?vehicleName=${vehicleName}`
            );
            setTimeout(() => {
              navigate(
                `/spider-web/createTask?taskCode=${res.data.taskCode}&vehicleName=${vehicleName}&vehicleScheduleState=${VehicleScheduleState.ALREADY}`
              );
              // 导航完成后，延迟解除阻止，确保导航已经执行
              setTimeout(() => {
                sendGlobalEvent(UNBLOCK_ROUTE_NAVIGATION);
              }, 100);
            }, 1000);
          } else {
            navigate(
              `/spider-web/createTask?taskCode=${res?.data?.taskCode}&vehicleName=${vehicleName}&vehicleScheduleState=${VehicleScheduleState.ALREADY}`
            );
            // 导航完成后，延迟解除阻止，确保导航已经执行
            setTimeout(() => {
              sendGlobalEvent(UNBLOCK_ROUTE_NAVIGATION);
            }, 100);
          }

          setPopoverVisible(false);
        } else {
          showModal({
            content: '创建失败，车辆已有任务，请完成或取消现有任务后重试',
            footer: [
              {
                type: 'primary',
                btnText: '我知道了',
                autoClose: true
              }
            ]
          });
          // 失败时也延迟解除阻止，确保不会有竞态条件
          setTimeout(() => {
            sendGlobalEvent(UNBLOCK_ROUTE_NAVIGATION);
          }, 500);
        }
        setTimeout(() => {
          Toast.clear();
        }, 1500);
      } catch (err) {
        // 异常情况下也解除阻止
        setTimeout(() => {
          sendGlobalEvent(UNBLOCK_ROUTE_NAVIGATION);
        }, 500);
      }
    }),
    [createTaskApi, isGeneralTransportSingleVehicle, navigate]
  );

  /**
   * 点击创建蛛网任务
   * 先校验车辆是否存在问题，不存在问题则创建蛛网任务
   * @return {*}
   */
  const validateVehicleHasNoIssueBeforeCreateTask = () => {
    const hasNoIssue = validateVehicleHasNoIssue({
      issueType: spiderWebNavbarInfo?.vehicleIssueType || null,
      passValidationCallbackFunc: startCreateTask,
      getVehicleRealtimeInfo: spiderWebNavbarInfo?.getVehicleRealtimeInfo,
      verificationTiming: 'createTask'
    });
    if (hasNoIssue) startCreateTask();
  };

  return (
    <Button
      color="default"
      fill="none"
      size="small"
      onClick={validateVehicleHasNoIssueBeforeCreateTask}
    >
      创建“蛛网”任务
    </Button>
  );
}

export default SpiderWebEntrance;
